import { PartyPopper, Landmark, Briefcase, Sparkles, UtensilsCrossed, Trophy, Paintbrush, Camera, Music, Swords } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { scrollToId } from "@/App";

const AUDIENCES = [
  { icon: PartyPopper, title: "Individuals & Social Groups", desc: "Birthdays, hens nights, reunions and social catch-ups. Any occasion, any group size." },
  { icon: Landmark, title: "Councils & Community", desc: "Community activations, holiday programs, festivals and public space events across Melbourne." },
  { icon: Briefcase, title: "Corporate & Workplace", desc: "Team days, end-of-year events, product launches and wellness programs at your office or venue." },
  { icon: Sparkles, title: "Events & Agencies", desc: "Shopping centres, concerts, fairs, charity days, brand launches and property activations." },
];

const STEPS = [
  { n: "01", title: "Get a quote", tag: "1HR TURNAROUND", desc: "Tell us your date, location and what you're planning. We'll send a tailored proposal within the hour." },
  { n: "02", title: "We handle everything", tag: "FULL SERVICE", desc: "Court, net, paddles, balls, delivery, setup, hosting and pack-down. We just need access to your venue." },
  { n: "03", title: "Everyone plays", tag: "INSTANT ENGAGEMENT", desc: "All ages, all abilities. From first-timers to enthusiasts, everyone's rallying within minutes." },
];

const ADDONS = [
  { icon: UtensilsCrossed, title: "Food & Beverage", price: "Packages", desc: "Full F&B including alcohol through our liquor licence — grazing boards to full catering." },
  { icon: Trophy, title: "Prizes & Trophies", price: "From $15pp", desc: "Custom trophies, medals and prize packs for your winners." },
  { icon: Paintbrush, title: "Custom Branding", price: "Enquire", desc: "Branded court banners, custom paddles, signage and merch. Lead times apply." },
  { icon: Camera, title: "Event Photography", price: "POA", desc: "Professional photography to capture every rally of the day." },
  { icon: Music, title: "Entertainment", price: "POA", desc: "DJs, live music and roving entertainment to lift the energy." },
  { icon: Swords, title: "Tournament Format", price: "Included option", desc: "Structured round-robin tournaments run by our hosts — perfect for competitive groups." },
];

export default function InfoSections() {
  return (
    <>
      <section className="relative py-24 lg:py-32" data-testid="audiences-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="Who books us" title="For every occasion. For everyone." testid="audiences-heading" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <div
                  data-testid="audience-card"
                  className="group h-full rounded-3xl border border-white/10 bg-carbon p-8 transition-colors duration-300 hover:border-volt/40"
                >
                  <a.icon size={26} className="mb-6 text-volt" />
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="process" className="relative bg-carbon py-24 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="The process" title="Three steps. Zero complexity." testid="process-heading" />
          <div className="grid gap-4 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div
                  data-testid="process-step-item"
                  className="relative h-full overflow-hidden rounded-3xl border border-white/10 bg-panel p-8 lg:p-10"
                >
                  <span className="pointer-events-none absolute -right-3 -top-7 font-display text-[7rem] font-extrabold leading-none text-white/5">
                    {s.n}
                  </span>
                  <p className="font-mono text-sm font-semibold tracking-[0.25em] text-volt">{s.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white">{s.title}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{s.tag}</p>
                  <p className="mt-4 text-sm leading-relaxed text-white/60 sm:text-base">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="addons" className="relative py-24 lg:py-32" data-testid="addons-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="Make it your own" title="Add-ons to level up your event" testid="addons-heading" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ADDONS.map((a, i) => (
              <Reveal key={a.title} delay={(i % 3) * 0.08}>
                <div
                  data-testid="addon-card"
                  className="group h-full rounded-3xl border border-white/10 bg-carbon p-8 transition-colors duration-300 hover:border-volt/40"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <a.icon size={24} className="text-volt" />
                    <span className="rounded-full border border-volt/30 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-volt">
                      {a.price}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <button
              data-testid="addons-enquire-button"
              onClick={() => scrollToId("#quote")}
              className="rounded-full border border-white/25 px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-volt hover:text-volt"
            >
              Enquire about add-ons
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
